import React from 'react';
import { Card, CardContent, Typography, Box, Alert, Autocomplete, TextField } from '@mui/material';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import moment from 'moment-timezone';

interface AvailabilitySelectionSectionProps {
  selectedTimezone: string;
  onTimezoneChange: (timezone: string) => void;
  surveyDuration: string;
}

const auxTimezones = moment.tz.names().map((tz) => {
  const now = moment.tz(tz);
  const abbr = now.zoneAbbr(); // Try getting named zone

  // Replace underscores for readability
  const label = tz.replace(/_/g, ' ');

  let display: string;

  // If abbr is missing or is just GMT (without offset), use formatted UTC offset
  if (!abbr || abbr === 'GMT' || /^[+-]\d+$/.test(abbr)) {
    const offsetMinutes = now.utcOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offsetMinutes) / 60)
      .toString()
      .padStart(2, '0');
    const minutes = (Math.abs(offsetMinutes) % 60).toString().padStart(2, '0');
    display = `UTC${sign}${hours}:${minutes}`;
  } else {
    display = abbr;
  }

  return {
    value: tz,
    label: `${label} (${display})`,
  };
});

// Split into US and others
const usTimezones = auxTimezones.filter((tz) => tz.value.startsWith('US/'));
const otherTimezones = auxTimezones.filter((tz) => !tz.value.startsWith('US/'));

// Sort both groups alphabetically by label
usTimezones.sort((a, b) => a.label.localeCompare(b.label));
otherTimezones.sort((a, b) => a.label.localeCompare(b.label));

// Combine
const timezones = [...usTimezones, ...otherTimezones];

export const AvailabilitySelectionSection = ({
  selectedTimezone,
  onTimezoneChange,
  surveyDuration,
}: AvailabilitySelectionSectionProps): JSX.Element => {
  const selectedOption =
    timezones.find((tz) => tz.value.toLowerCase() === selectedTimezone.toLowerCase()) ?? null;
  return (
    <Box sx={{ px: '0', py: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Card sx={{ width: '100%', boxShadow: 'none' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert
            severity="success"
            icon={<CelebrationOutlinedIcon sx={{ fontSize: '20px' }} />}
            sx={{
              bgcolor: '#e6f5eb',
              border: '1px solid #c3e5cd',
              color: '#065b2c',
              py: 1,
              maxHeight: '36px',
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              '& .MuiAlert-message': {
                overflow: 'hidden',
                padding: '8px 0',
                minWidth: 0,
              },
            }}
          >
            <Typography variant="body2" sx={{ color: '#065b2c', fontSize: '11px' }}>
              You&apos;ve passed the screener
            </Typography>
          </Alert>

          <Typography variant="h3" fontWeight="bold">
            Select Your Availability
          </Typography>

          <Typography variant="body2" sx={{ color: '#384151' }}>
            Choose multiple dates and times that work for your {surveyDuration}-minute consultation
            call. The more availability you provide, the easier it will be to find a time that works
            for everyone.
          </Typography>

          <Box sx={{ maxWidth: 400 }}>
            <Autocomplete
              value={selectedOption}
              onChange={(event, newValue) => {
                if (newValue) {
                  onTimezoneChange(newValue.value);
                }
              }}
              options={timezones}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Timezone"
                  placeholder="Search timezones..."
                  size="small"
                  InputLabelProps={{
                    sx: { fontSize: '14px' },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      fontSize: '14px',
                    },
                    startAdornment: (
                      <>
                        <LanguageOutlinedIcon sx={{ mr: 1, color: '#6b7280' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '14px' }}>
                    <LanguageOutlinedIcon fontSize="small" />
                    {option.label}
                  </Box>
                </li>
              )}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
